import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { createFormChangeTracker } from '@changeTracking/utils/createFormChangeTracker';
import { findChangedForms } from '@changeTracking/utils/findChangedForms';
import type { CustomerContract } from '@store/customers/customerDetails/contracts';
import type {
  ChangedFieldPathToken,
  ChangeTrackingSource,
} from '@typeDefinitions/changeTrackingTypes';

const BASIC_INFORMATION_FIELD_SOURCES: Partial<
  Record<string, ChangeTrackingSource>
> = {
  agreementDate:
    CHANGE_TRACKING_SOURCES.customerDetails.contractsAgreementEffectiveDate,
  countryOption:
    CHANGE_TRACKING_SOURCES.customerDetails.contractsCountryOfIncorporation,
  customerEntityType:
    CHANGE_TRACKING_SOURCES.customerDetails.contractsCustomerEntityType,
  isUsaEntity:
    CHANGE_TRACKING_SOURCES.customerDetails.contractsIsCustomerUsEntity,
};

const INSURANCE_FIELDS = new Set([
  'bipd',
  'brokerAllRiskEnabled',
  'carrierAllRiskEnabled',
  'carrierAutoLiability',
  'carrierCyber',
  'carrierEnv',
  'carrierGeneralLiability',
  'carrierOtherInsurance',
  'carrierOtherInsuranceEnabled',
  'contingentCargo',
  'generalAuto',
  'generalCyber',
  'generalEnv',
  'generalLiability',
  'generalOtherInsurance',
  'generalOtherInsuranceEnabled',
  'isShipperSelfInsured',
  'primaryCargo',
  'refrigeratedCargo',
  'workersCompEnabled',
  'workersCompOption',
]);

const TERM_AND_PAYMENT_FIELDS = new Set([
  'contractTermTimeframe',
  'paymentTermOption',
]);

const CLAIMS_AND_CONCEALED_DAMAGES_FIELDS = new Set([
  'claimValueBasis',
  'claimsPaidTimeframe',
  'concealedDamageReportTimeframe',
  'customerRightOffsetInvoice',
  'damageReportTimeframe',
  'refusalNotificationTimeframe',
  'shipperFileTimeframe',
]);

const ADDITIONAL_LANGUAGE_FIELDS = new Set([
  'governingLawState',
  'otherLanguage',
  'proposedRateConfirmationLanguage',
]);

const TEMPERATURE_CONTROLLED_FIELDS = new Set([
  'requiresSeal',
  'requiresTemperatureRecorder',
]);

const OTHER_CARRIER_REQUIREMENTS_FIELDS = new Set([
  'carbCompliant',
  'coBrokersPermitted',
  'ctpatCertified',
  'driverSpeaksEnglish',
  'gpsTracking',
  'reeferDownload',
  'satisfactoryRating',
  'smartWayCertified',
]);

const CARRIER_BROKER_LIABILITY_FIELDS = new Set([
  'brokerAssumesCarrierLiability',
  'carrierAssumesFullLiability',
]);

const BROKER_RESPONSIBILITY_FIELDS = new Set([
  'brokerGPSTracking',
  'brokerPODWithin24Hours',
  'brokerProvideUpdatesInTransit',
  'brokerScheduleAppointments',
]);

const ACCESSORIAL_BILLING_FIELDS = new Set([
  'accessorialSheetProvided',
  'rateChangeApprovals',
  'rateTender',
  'receipts',
  'signedBOLRequired',
]);

const INDEMNIFICATION_FIELDS = new Set(['brokerIndemnification']);

const isEmptyContractValue = (value: unknown): boolean =>
  value === null || value === undefined || value === '';

const getPropertyPath = (path: readonly ChangedFieldPathToken[]): string[] =>
  path.flatMap((token) => (token.type === 'property' ? [token.key] : []));

const areContractScalarValuesEqual = (
  before: unknown,
  after: unknown,
): boolean => {
  if (Object.is(before, after)) return true;
  return isEmptyContractValue(before) && isEmptyContractValue(after);
};

const getContractForm = (
  path: readonly ChangedFieldPathToken[],
): ChangeTrackingSource => {
  const field = getPropertyPath(path).at(0);
  if (field === undefined)
    return CHANGE_TRACKING_SOURCES.customerDetails.contracts;

  const basicInformationSource = BASIC_INFORMATION_FIELD_SOURCES[field];
  if (basicInformationSource !== undefined) {
    return basicInformationSource;
  }

  if (INSURANCE_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.contractsInsurance;
  }

  if (TERM_AND_PAYMENT_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.contractsTermAndPaymentTerms;
  }

  if (CLAIMS_AND_CONCEALED_DAMAGES_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails
      .contractsClaimsAndConcealedDamages;
  }

  if (ADDITIONAL_LANGUAGE_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.contractsAdditionalLanguage;
  }

  if (TEMPERATURE_CONTROLLED_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails
      .contractsTemperatureControlledCommodities;
  }

  if (OTHER_CARRIER_REQUIREMENTS_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails
      .contractsOtherCarrierRequirements;
  }

  if (CARRIER_BROKER_LIABILITY_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails
      .contractsCarrierBrokerLiability;
  }

  if (BROKER_RESPONSIBILITY_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails
      .contractsBrokerResponsibilities;
  }

  if (ACCESSORIAL_BILLING_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails
      .contractsAccessorialBillingInformation;
  }

  if (INDEMNIFICATION_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.contractsIndemnification;
  }

  return CHANGE_TRACKING_SOURCES.customerDetails.contracts;
};

const findChangedContractForms = (
  before: CustomerContract,
  after: CustomerContract,
): ChangeTrackingSource[] =>
  findChangedForms(before, after, {
    areScalarValuesEqual: areContractScalarValuesEqual,
    ignoredFields: new Set(['customerContractId', 'customerId', 'isNew']),
    getForm: (change) => getContractForm(change.path),
  });

const contractFormChangeTracker = createFormChangeTracker<
  CustomerContract,
  ChangeTrackingSource
>({
  getIdentity: (contract) => contract.customerId,
  findChangedForms: findChangedContractForms,
});

export const setCustomerContractChangeBaseline =
  contractFormChangeTracker.setBaseline;
export const prepareCustomerContractChangeSummary =
  contractFormChangeTracker.prepare;
